import * as types from '@/store/mutationTypes';
import sessionManager from '@/store/modules/session/sessionManager';

const waitForToken = (store, getTokenFn) => {
  let unwatch;
  return Promise.race([
    new Promise((resolve) => {
      if (getTokenFn()) {
        resolve();
        return;
      }
      unwatch = store.watch(getTokenFn, (val) => {
        if (val) {
          unwatch();
          resolve();
        }
      });
    }),
    new Promise((resolve, reject) =>
      setTimeout(() => {
        if (unwatch) unwatch();
        reject(new Error('Token wait timeout'));
      }, 10000)
    )
  ]);
};

const waitForAuthToken = async (store, rootState, rootGetters) => {
  // wait for hub/isHubConfigLoaded
  await new Promise((resolve) => {
    if (!rootState.hub || rootState.hub.isHubConfigLoaded) {
      resolve();
      return;
    }
    const unwatch = store.watch(
      (state) => state.hub && state.hub.isHubConfigLoaded,
      (val) => {
        if (val) {
          unwatch();
          resolve();
        }
      }
    );
  });

  // for Auth0 only, also wait for cs-session token to be established
  // Hub token is managed entirely by CSDS — waiting for it risks triggering signOutImpl on expiry
  if (rootGetters['auth0/enabled']) {
    try {
      await waitForToken(store, () => rootState['cs-session'] && rootState['cs-session'].token);
    } catch (e) {
      console.error('waitForAuthToken:', e.message);
    }
  }
};

export default {
  async logout({ rootGetters, dispatch }, query) {
    await dispatch('ahaFeedback/logoutAhaPortal', {}, { root: true });
    if (rootGetters['hub/isEnabled']) {
      await dispatch('hub-session/logout', {}, { root: true });
    } else if (rootGetters['auth0/enabled']) {
      localStorage.setItem('redirect', window.location.pathname);
      await dispatch('cs-session/logout', false, { root: true });
    } else {
      await dispatch('session/logout', query, { root: true });
    }
  },
  async getUserProfile({ commit }, stbApiAdapter) {
    const cbResponse = await stbApiAdapter.getUserAuth({ store: this.$store });
    if (!cbResponse.error) {
      commit(types.SET_USER_PROFILE, cbResponse.data.computed);
    }
  },

  async initCrossTenantSession({ dispatch, getters, rootState, rootGetters }) {
    await waitForAuthToken(this, rootState, rootGetters);
    await dispatch('cross-tenant/fetchCrossTenantSessionInfo', {}, { root: true });
    if (!getters.isLoggedIn) {
      return;
    }
    const isRootClient = sessionManager.getClientSubdomain() === '';

    if (isRootClient) {
      await dispatch('cross-tenant/initiateCrossTenantSession', {}, { root: true });
    }
    await dispatch('cross-tenant/fetchCrossTenantSessionInfo', {}, { root: true });
  },
  async validateAndSetUserTokenOrLogout({ dispatch, getters, rootState, rootGetters }, argHash) {
    let isCrossTenantSession = rootGetters['cross-tenant/isCrossTenantSession'];
    const isRootClient = rootGetters['session/isRootClient'];

    if (isCrossTenantSession === undefined) {
      // To call a cross-tenant Vuex action, use `dispatch` with root option enabled.
      isCrossTenantSession = await dispatch(
        'cross-tenant/fetchCrossTenantSessionInfo',
        {},
        { root: true }
      );
    }

    if (isCrossTenantSession !== true) {
      await waitForAuthToken(this, rootState, rootGetters);
    }
    const { query, axiosInstance } = argHash;
    if (!getters.isLoggedIn) {
      if (!isRootClient && isCrossTenantSession === true) {
        return;
      }
      await dispatch('logout', query);
      return;
    }

    if (getters.hubEnabled || getters.auth0Enabled || getters.crossTenantSessionEnabled) return;
    let token = '';
    if (isRootClient || isCrossTenantSession === false) {
      token = `Bearer ${sessionManager.getAuthTokenFromCookie()}`;
    }
    axiosInstance.interceptors.request.use(
      (config) => {
        // eslint-disable-next-line no-param-reassign
        config.headers.authorization = token;
        config.withCredentials = true;
        return config;
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }
};
